import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  Wrench,
  Phone,
  ClipboardList,
  KeyRound,
  Users,
  Briefcase,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const TIPOS = [
  { value: 'tecnico', label: 'Técnico', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 border-blue-300' },
  { value: 'ayudante', label: 'Ayudante', badge: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300 border-purple-300' },
  { value: 'oficina', label: 'Oficina', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-300' },
];
const TIPO_LABEL = Object.fromEntries(TIPOS.map(t => [t.value, t.label]));
const TIPO_BADGE = Object.fromEntries(TIPOS.map(t => [t.value, t.badge]));

const NIVELES = [1, 2, 3, 4, 5, 6];

const ESPECIALIDADES = [
  { value: 'presupuestos', label: 'Presupuestos' },
  { value: 'labores_generales', label: 'Labores Generales' },
  { value: 'obras', label: 'Obras' },
  { value: 'solo_tecnico', label: 'Solo Técnico' },
  { value: 'solo_ayudante', label: 'Solo Ayudante' },
];
const ESP_LABEL = Object.fromEntries(ESPECIALIDADES.map(e => [e.value, e.label]));
const ESP_VALUES = ESPECIALIDADES.map(e => e.value);
const displaySpecialty = (raw) => {
  if (!raw) return '';
  return ESP_LABEL[raw] || raw; // texto libre legacy se muestra tal cual
};

const tipoOf = (t) => t.tipo || 'tecnico';

function getEmptyForm() {
  return {
    name: '',
    phone: '',
    specialty: '',
    tipo: 'tecnico',
    nivel: null,
    is_active: true,
    comments: '',
  };
}

export default function Technicians() {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterNivel, setFilterNivel] = useState('all');
  const [filterEsp, setFilterEsp] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTech, setSelectedTech] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [saving, setSaving] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({ tech_id: '', tech_name: '', username: '', password: '', password2: '', role: 'tecnico', existingUser: null });
  const [savingUser, setSavingUser] = useState(false);

  useEffect(() => {
    fetchTechnicians();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const fetchTechnicians = async () => {
    try {
      const res = await axios.get(`${API}/technicians?include_inactive=${showInactive}`);
      setTechnicians(res.data);
    } catch (error) {
      toast.error('Error al cargar trabajadores');
    } finally {
      setLoading(false);
    }
  };

  const openNewDialog = () => {
    setSelectedTech(null);
    setFormData(getEmptyForm());
    setDialogOpen(true);
  };

  const openEditDialog = (tech) => {
    setSelectedTech(tech);
    setFormData({
      name: tech.name || '',
      phone: tech.phone || '',
      specialty: tech.specialty || '',
      tipo: tipoOf(tech),
      nivel: typeof tech.nivel === 'number' ? tech.nivel : null,
      is_active: tech.is_active !== false,
      comments: tech.comments || '',
    });
    setDialogOpen(true);
  };

  const openUserDialog = async (tech) => {
    let existingUser = null;
    try {
      const r = await axios.get(`${API}/users`);
      existingUser = (r.data || []).find(u => u.technician_id === tech.id) || null;
    } catch { /* ignore */ }
    const defaultRole = existingUser?.role || (tipoOf(tech) === 'oficina' ? 'operador' : tipoOf(tech));
    setUserForm({
      tech_id: tech.id,
      tech_name: tech.name,
      username: existingUser?.username || (tech.name || '').split(' ')[0].toLowerCase(),
      password: '',
      password2: '',
      role: defaultRole,
      existingUser,
    });
    setUserDialogOpen(true);
  };

  const submitUserForm = async (e) => {
    e.preventDefault();
    if (!userForm.existingUser) {
      if (!userForm.password || userForm.password.length < 4) { toast.error('Contraseña debe tener al menos 4 caracteres'); return; }
      if (userForm.password !== userForm.password2) { toast.error('Las contraseñas no coinciden'); return; }
    }
    setSavingUser(true);
    try {
      if (userForm.existingUser) {
        const patch = { role: userForm.role };
        if (userForm.password) {
          if (userForm.password !== userForm.password2) { toast.error('Las contraseñas no coinciden'); setSavingUser(false); return; }
          patch.password = userForm.password;
        }
        await axios.put(`${API}/users/${userForm.existingUser.id}`, patch);
        toast.success('Usuario actualizado');
      } else {
        await axios.post(`${API}/technicians/${userForm.tech_id}/create-user`, {
          username: userForm.username,
          password: userForm.password,
          role: userForm.role,
        });
        toast.success('Usuario creado y vinculado');
      }
      setUserDialogOpen(false);
      fetchTechnicians();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSavingUser(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      // Oficina no maneja nivel ni especialidad
      const payload = { ...formData };
      if (payload.tipo === 'oficina') {
        payload.nivel = null;
        payload.specialty = '';
      }
      if (selectedTech) {
        await axios.put(`${API}/technicians/${selectedTech.id}`, payload);
        toast.success('Trabajador actualizado');
      } else {
        await axios.post(`${API}/technicians`, payload);
        toast.success('Trabajador creado');
      }
      setDialogOpen(false);
      fetchTechnicians();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedTech) return;
    try {
      await axios.delete(`${API}/technicians/${selectedTech.id}`);
      toast.success('Trabajador desactivado');
      setDeleteDialogOpen(false);
      setSelectedTech(null);
      fetchTechnicians();
    } catch (error) {
      toast.error('Error al desactivar');
    }
  };

  const counts = useMemo(() => {
    const c = { all: technicians.length, tecnico: 0, ayudante: 0, oficina: 0 };
    technicians.forEach(t => { const tp = tipoOf(t); if (c[tp] !== undefined) c[tp]++; });
    return c;
  }, [technicians]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return technicians.filter(t => {
      if (filterTipo !== 'all' && tipoOf(t) !== filterTipo) return false;
      if (filterNivel !== 'all') {
        if (String(t.nivel) !== filterNivel) return false;
      }
      if (filterEsp !== 'all') {
        if ((t.specialty || '') !== filterEsp) return false;
      }
      if (q) {
        const hay = `${t.name || ''} ${t.phone || ''} ${t.specialty || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [technicians, search, filterTipo, filterNivel, filterEsp]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isOficinaForm = formData.tipo === 'oficina';

  return (
    <div className="space-y-4 animate-fade-in" data-testid="workers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6 text-primary" />Trabajadores</h2>
          <p className="text-muted-foreground text-sm">{filtered.length} de {technicians.length} trabajadores</p>
        </div>
        <Button onClick={openNewDialog} data-testid="new-worker-button">
          <Plus className="h-4 w-4 mr-2" />
          Agregar Trabajador
        </Button>
      </div>

      {/* Chips filtro por tipo */}
      <div className="flex flex-wrap gap-2" data-testid="tipo-chips">
        <Button size="sm" variant={filterTipo === 'all' ? 'default' : 'outline'} onClick={() => setFilterTipo('all')} data-testid="chip-all">Todos <Badge variant="secondary" className="ml-2">{counts.all}</Badge></Button>
        <Button size="sm" variant={filterTipo === 'tecnico' ? 'default' : 'outline'} onClick={() => setFilterTipo('tecnico')} data-testid="chip-tecnico">Técnicos <Badge variant="secondary" className="ml-2">{counts.tecnico}</Badge></Button>
        <Button size="sm" variant={filterTipo === 'ayudante' ? 'default' : 'outline'} onClick={() => setFilterTipo('ayudante')} data-testid="chip-ayudante">Ayudantes <Badge variant="secondary" className="ml-2">{counts.ayudante}</Badge></Button>
        <Button size="sm" variant={filterTipo === 'oficina' ? 'default' : 'outline'} onClick={() => setFilterTipo('oficina')} data-testid="chip-oficina">Oficina <Badge variant="secondary" className="ml-2">{counts.oficina}</Badge></Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, teléfono o especialidad..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="worker-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="show-inactive"
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
              <Label htmlFor="show-inactive" className="text-sm">Mostrar inactivos</Label>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Nivel</Label>
              <Select value={filterNivel} onValueChange={setFilterNivel}>
                <SelectTrigger className="w-[160px]" data-testid="filter-nivel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los niveles</SelectItem>
                  {NIVELES.map(n => <SelectItem key={n} value={String(n)}>Nivel {n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Especialidad</Label>
              <Select value={filterEsp} onValueChange={setFilterEsp}>
                <SelectTrigger className="w-[200px]" data-testid="filter-esp"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las especialidades</SelectItem>
                  {ESPECIALIDADES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(filterTipo !== 'all' || filterNivel !== 'all' || filterEsp !== 'all' || search) && (
              <Button variant="ghost" size="sm" className="self-end" onClick={() => { setFilterTipo('all'); setFilterNivel('all'); setFilterEsp('all'); setSearch(''); }}>Limpiar filtros</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Workers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No se encontraron trabajadores</h3>
              <p className="text-muted-foreground">
                {search || filterTipo !== 'all' || filterNivel !== 'all' || filterEsp !== 'all' ? 'Intenta ajustar los filtros' : 'Agrega tu primer trabajador'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filtered.map((tech) => {
            const tp = tipoOf(tech);
            const isOficina = tp === 'oficina';
            const IconWorker = isOficina ? Briefcase : Wrench;
            return (
              <Card
                key={tech.id}
                className={`${!tech.is_active ? 'opacity-60' : ''}`}
                data-testid={`worker-card-${tech.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${tech.is_active ? 'bg-primary/10' : 'bg-muted'}`}>
                        <IconWorker className={`h-5 w-5 ${tech.is_active ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold truncate">{tech.name}</h3>
                        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                          <Badge variant="outline" className={`text-xs ${TIPO_BADGE[tp] || ''}`}>{TIPO_LABEL[tp] || tp}</Badge>
                          {!isOficina && typeof tech.nivel === 'number' && (
                            <Badge variant="outline" className="text-xs">Nivel {tech.nivel}</Badge>
                          )}
                          {!tech.is_active && <Badge variant="secondary" className="text-xs">Inactivo</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Crear/editar usuario de acceso"
                        onClick={() => openUserDialog(tech)}
                        data-testid={`worker-user-btn-${tech.id}`}
                      >
                        <KeyRound className="h-4 w-4 text-blue-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEditDialog(tech)}
                        data-testid={`worker-edit-btn-${tech.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {tech.is_active && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => { setSelectedTech(tech); setDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 space-y-1.5 text-sm">
                    {!isOficina && (tech.specialty ? (
                      <div className="text-muted-foreground"><span className="font-medium text-foreground">Especialidad:</span> {displaySpecialty(tech.specialty)}</div>
                    ) : (
                      <div className="text-muted-foreground italic">Sin especialidad</div>
                    ))}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      {tech.phone || 'Sin teléfono'}
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <ClipboardList className="h-4 w-4" />
                      {tech.services_count || 0} servicios realizados
                    </div>
                  </div>

                  {tech.comments && (
                    <p className="mt-3 text-sm text-muted-foreground border-t pt-3">
                      {tech.comments}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Worker Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedTech ? 'Editar Trabajador' : 'Agregar Trabajador'}</DialogTitle>
            <DialogDescription>
              {selectedTech ? 'Modifica los datos del trabajador' : 'Completa los datos para agregar un nuevo trabajador'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre completo"
                required
                data-testid="worker-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Número de teléfono"
                data-testid="worker-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo de trabajador *</Label>
              <div className="flex gap-2 flex-wrap">
                {TIPOS.map(t => (
                  <Button
                    key={t.value}
                    type="button"
                    size="sm"
                    variant={formData.tipo === t.value ? 'default' : 'outline'}
                    onClick={() => setFormData({ ...formData, tipo: t.value, ...(t.value === 'oficina' ? { nivel: null, specialty: '' } : {}) })}
                    data-testid={`form-tipo-${t.value}`}
                  >
                    {t.label}
                  </Button>
                ))}
              </div>
            </div>

            {!isOficinaForm && (
              <>
                <div className="space-y-2">
                  <Label>Nivel</Label>
                  <Select
                    value={formData.nivel != null ? String(formData.nivel) : 'none'}
                    onValueChange={(v) => setFormData({ ...formData, nivel: v === 'none' ? null : parseInt(v, 10) })}
                  >
                    <SelectTrigger data-testid="worker-nivel"><SelectValue placeholder="Sin nivel" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin nivel</SelectItem>
                      {NIVELES.map(n => <SelectItem key={n} value={String(n)}>Nivel {n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Especialidad</Label>
                  <Select
                    value={ESP_VALUES.includes(formData.specialty) ? formData.specialty : (formData.specialty ? 'legacy' : 'none')}
                    onValueChange={(v) => setFormData({ ...formData, specialty: v === 'none' || v === 'legacy' ? formData.specialty : v })}
                  >
                    <SelectTrigger data-testid="worker-especialidad"><SelectValue placeholder="Sin especialidad" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin especialidad</SelectItem>
                      {formData.specialty && !ESP_VALUES.includes(formData.specialty) && (
                        <SelectItem value="legacy">Actual: {formData.specialty}</SelectItem>
                      )}
                      {ESPECIALIDADES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="flex items-center gap-2">
              <Switch
                id="is-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is-active">Activo</Label>
            </div>

            <div className="space-y-2">
              <Label>Comentarios</Label>
              <Textarea
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                placeholder="Notas sobre el trabajador"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-worker-button">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  selectedTech ? 'Actualizar' : 'Agregar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desactivar Trabajador</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas desactivar a {selectedTech?.name}? El trabajador no aparecerá al asignar servicios pero se conservará su historial.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeactivate}>
              Desactivar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* USER DIALOG — crear/editar usuario para un trabajador existente */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-blue-600" />{userForm.existingUser ? 'Editar usuario de acceso' : 'Crear usuario de acceso'}</DialogTitle>
            <DialogDescription>Trabajador: <strong>{userForm.tech_name}</strong>{userForm.existingUser ? ` — vinculado a @${userForm.existingUser.username}` : ''}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitUserForm} className="space-y-3">
            {!userForm.existingUser && (
              <div><Label className="text-xs">Usuario *</Label><Input value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} required data-testid="user-username" /></div>
            )}
            <div><Label className="text-xs">{userForm.existingUser ? 'Nueva contraseña (opcional)' : 'Contraseña *'}</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required={!userForm.existingUser} data-testid="user-password" /></div>
            <div><Label className="text-xs">Confirmar contraseña {userForm.existingUser ? '' : '*'}</Label><Input type="password" value={userForm.password2} onChange={(e) => setUserForm({ ...userForm, password2: e.target.value })} required={!userForm.existingUser} /></div>
            <div><Label className="text-xs">Rol *</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tecnico">Técnico</SelectItem>
                  <SelectItem value="ayudante">Ayudante</SelectItem>
                  <SelectItem value="operador">Operador (Oficina)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">Técnicos y Ayudantes al iniciar sesión sólo verán la pantalla de Asistencia. Los operadores acceden al panel completo.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={savingUser} data-testid="user-submit">{savingUser ? 'Guardando...' : (userForm.existingUser ? 'Guardar cambios' : 'Crear usuario')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
